////////////////////////////////////////////////////////////////////////////////
// Scroll
////////////////////////////////////////////////////////////////////////////////

/**
 * A small collection of methods for querying window scroll bar values and
 *
 * @author Mark Notton <mark@marknotton.uk>
 * @author Will Po <https://github.com/willmcpo/body-scroll-lock>
 *
 * @license Copyright 2019 Mark Notton
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

class Scroll {

  // ---------------------------------------------------------------------------
  // Y Location
  // ---------------------------------------------------------------------------

  /**
  *  Returns the y location of window scroll bar
  * @method  y
  * @example Scroll.y
  * @return {Number} Y location in pixels, number will not be suffixed with 'px'
  */

  static get y() { return window.pageYOffset || document.documentElement.scrollTop }

  // ---------------------------------------------------------------------------
  // X Location
  // ---------------------------------------------------------------------------

  /**
  * Returns the x location of window scroll bar
  * @method  x
  * @example Scroll.x
  * @return {Number} X location in pixels, number will not be suffixed with 'px'
  */

  static get x() { return window.pageXOffset || document.documentElement.scrollLeft }

  // ---------------------------------------------------------------------------
  // Scroll bar width
  // ---------------------------------------------------------------------------

  /**
  * Returns width of the scroll bar
  * @method  width
  * @example Scroll.width
  * @return {Number} Width in pixels, number will not be suffixed with 'px'
  */

  static get width() { return window.innerWidth - document.documentElement.clientWidth }

  // ---------------------------------------------------------------------------
  // Y Position
  // ---------------------------------------------------------------------------

  /**
  * Returns vertical positioning of the scrollbar relative to the viewport in percent at 2 decimal places
  * @method  positionY
  * @example Scroll.positionY
  * @return {Number} Between 0 to 100, no '%' symbol will be suffixed.
  */

  static get positionY() {
    return Math.round(((document.documentElement.scrollTop + document.body.scrollTop) / (document.documentElement.scrollHeight - document.documentElement.clientHeight) * 100) * 100) / 100
  }

  // ---------------------------------------------------------------------------
  // Scroll To Location
  // ---------------------------------------------------------------------------

  /**
  *  Returns the y location of window scroll bar
  * @see https://gist.github.com/felipenmoura/650e7e1292c1e7638bcf6c9f9aeb9dd5
  * @method to
  * @param
  * @param {Object} target ...
  * @param {Object} container ...
  * @param {Object} duration ...
  * @param {Object} position ...
  * @param {Object} offset ...
  * @param {Object} callback ...
  * @example Scroll.to(...)
  * @return {Number} Y location in pixels, number will not be suffixed with 'px'
  */

  static to() {

    let target, container, duration, position, offset, callback;

    Object.values(arguments).forEach((setting) => {

      let type = typeof setting

      if ( type == 'object' ) {

        if ( window.jQuery && setting instanceof jQuery ) {
          setting = setting[0]
        }
        if ( setting instanceof Node || setting instanceof Element ) {
          if ( !target ) { target = setting }
          else { container = setting }
        }

      } else if ( type == 'number' ) {

        if ( !offset ) { offset = setting }
        else if ( !position ) { position = setting }
        else if ( !duration ) { duration = setting }

      } else if ( type == 'string' ) {

        if ( !target ) { target = document.querySelector(setting) }
        else { container = document.querySelector(setting) }

      } else if ( type == 'function' ) {
        callback = setting;
      }

    })

    // Fallbacks
    // container = container || document.querySelector('site-wrapper') || document.scrollingElement
    container = container || document.scrollingElement
    target    = target    || container
    duration  = duration  || 500
    offset    = offset    || 0
    position  = position  || 0

    new Promise((resolve, reject) => {

      let endPosition = position || target.getBoundingClientRect().top + container.scrollTop || 0,
          startPosition = container.scrollTop,
          change = endPosition - startPosition + offset,
          currentTime = 0,
          increment = 20;

      const animateScroll = function() {
        currentTime += increment;
        let val = Math.easeInOutQuad(currentTime, startPosition, change, duration);
        container.scrollTop = val;
        if(currentTime < duration) {
          setTimeout(animateScroll, increment);
        } else {
          resolve()
          if ( callback ) { callback(target) }
        }
      };
      animateScroll();
    });

  }


  // ---------------------------------------------------------------------------
  // Returns the last known direction the window had scrolled.
  // ---------------------------------------------------------------------------

  /**
  * Returns the last known direction the window had scrolled.
  * @method  direction
  * @example Scroll.direction
  * @return {Array} ['up' or 'down', 'left' or 'right']
  */

  static get direction() {

    let results = [];

    // Vertical check
    let y = window.pageYOffset || document.documentElement.scrollTop;
    let v = y < Scroll.y ? 'up' : (y > Scroll.y ? 'down' : false);
    if (v !== false) { results.push(v) }
    Scroll.y = y <= 0 ? 0 : y;

    // Horizontal check
    let x = window.pageXOffset || document.documentElement.scrollLeft;
    let h = x < Scroll.x ? 'left' : (x > Scroll.x ? 'right' : false);
    if (h !== false) { results.push(h) }
    Scroll.x = x <= 0 ? 0 : x;

    return results;
  }
}
